import React, { createContext, useContext, useEffect, ReactNode, useState } from 'react'
import { useAccount } from '../../store/account/hooks'

interface AccountStateProviderProps {
    children: ReactNode
    autoLoad?: boolean
}

interface AccountContextType {
    account: ReturnType<typeof useAccount>
}

const AccountContext = createContext<AccountContextType | null>(null)

export const AccountStateProvider: React.FC<AccountStateProviderProps> = ({
    children,
    autoLoad = true
}) => {
    const account = useAccount()
    const [initialized, setInitialized] = useState(false)

    // Initial load effect - runs once on mount
    useEffect(() => {
        if (autoLoad && !initialized) {
            setInitialized(true)
            account.loadAccountData()
        }
    }, [autoLoad, initialized, account])

    const contextValue: AccountContextType = { account }

    return (
        <AccountContext.Provider value={contextValue}>
            {children}
        </AccountContext.Provider>
    )
}

export const useAccountContext = (): AccountContextType => {
    const context = useContext(AccountContext)
    if (!context) {
        throw new Error('useAccountContext must be used within an AccountStateProvider')
    }
    return context
}

export const useAccountState = () => {
    const { account } = useAccountContext()
    return account
}